import { useId, useState, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, ChoiceChips, cn, DialogFooter, Eyebrow, Text, Textarea } from "@athena/ui";
import { ClientSearchField, type ClientOption } from "./ClientSearchField";
import { FollowUpPicker } from "./FollowUpPicker";
import type { ContactChannel, ContactOutcome, FollowUpChoice } from "../helpers/client";
import { FOLLOW_UP_DATE_CHOICES, resolveFollowUp, CONTACT_CHANNELS, CONTACT_OUTCOMES } from "../helpers/client";

export interface LoggedContact {
    /** Who the contact was with: the form's `clientId`, or the one picked from `clients`. */
    clientId: string;
    channel: ContactChannel;
    outcome: ContactOutcome;
    note?: string;
    followUpOn?: Date;
}

/** The queue's buttons are sentence-case pills, as in the mockups, not the portal's uppercase blocks. */
export const PILL_BUTTON = "w-auto rounded-full normal-case tracking-normal";

const FOLLOW_UP_CHOICES: FollowUpChoice[] = [
    "none",
    ...FOLLOW_UP_DATE_CHOICES,
];

interface LogContactFormProps {
    today: Date;
    /**
     * "dialog" asks everything at once. "inline" — the queue's own row — asks one question at a
     * time: how they connected (unless the follow-up planned it), then how it went, then a note and
     * the next follow-up, so the row stays short until there is something to say.
     */
    layout: "dialog" | "inline";
    /** The one person this form is about. Give this or `clients`. */
    clientId?: string;
    /** Ask who the contact was with, from these — when the form is not about one person already. */
    clients?: ClientOption[];
    /** The channel a follow-up planned. The dialog starts with it picked; inline skips the question. */
    plannedChannel?: ContactChannel;
    /** Rejects when the contact could not be saved; the form keeps what was entered. */
    onSubmit: (contact: LoggedContact) => Promise<void>;
    /** The dialog's Cancel. */
    onCancel?: () => void;
    /** Inline only: the same contact in the full dialog. */
    onOpenFullLog?: () => void;
    /** What the form starts with: a logged contact, when it is being edited. A follow-up opens as its date. */
    initial?: Omit<LoggedContact, "clientId">;
    /** The submit button's text. Defaults to logging a new contact. */
    submitLabel?: string;
}

/** Log one contact — shared by the log-contact dialog and the outreach queue's inline row. */
export function LogContactForm({
    today,
    layout,
    clientId,
    clients,
    plannedChannel,
    onSubmit,
    onCancel,
    onOpenFullLog,
    initial,
    submitLabel,
}: LogContactFormProps) {
    const { t } = useTranslation();
    const ids = useId();
    const inline = layout === "inline";
    const [
        pickedClientId,
        setPickedClientId,
    ] = useState<string | undefined>();
    const [
        pickedChannel,
        setPickedChannel,
    ] = useState<ContactChannel | undefined>(initial?.channel ?? (inline ? undefined : plannedChannel));
    const [
        outcome,
        setOutcome,
    ] = useState<ContactOutcome | undefined>(initial?.outcome);
    const [
        note,
        setNote,
    ] = useState<string>(initial?.note ?? "");
    const [
        followUp,
        setFollowUp,
    ] = useState<FollowUpChoice>(initial?.followUpOn ? "custom" : "none");
    const [
        customDate,
        setCustomDate,
    ] = useState<Date | undefined>(initial?.followUpOn);
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    const contactClientId = clientId ?? pickedClientId;
    const channel = pickedChannel ?? (inline ? plannedChannel : undefined);
    const followUpOn = resolveFollowUp(followUp, customDate, today);
    const ready =
        contactClientId !== undefined &&
        channel !== undefined &&
        outcome !== undefined &&
        (followUp !== "custom" || customDate !== undefined);

    // Inline, each question waits for the one before it; the dialog shows them all.
    const askChannel = !inline || plannedChannel === undefined;
    const askOutcome = !inline || channel !== undefined;
    const askRest = !inline || outcome !== undefined;

    const submit = async () => {
        if (!ready || saving) {
            return;
        }
        setSaving(true);
        try {
            await onSubmit({
                clientId: contactClientId,
                channel,
                outcome,
                note: note.trim() || undefined,
                followUpOn,
            });
        } catch {
            // The caller has said what went wrong; keep what was entered so it can be sent again.
        } finally {
            setSaving(false);
        }
    };

    // Secondary blue in both layouts and both themes, the same as the other dialogs' submit.
    const submitButton = (
        <Button
            variant="secondary"
            className={inline ? PILL_BUTTON : undefined}
            disabled={!ready}
            loading={saving}
            onClick={submit}
        >
            {submitLabel ?? t("admin.alumni.logContact.submit")}
        </Button>
    );

    return (
        <div className={cn("flex flex-col", inline ? "gap-5" : "gap-6")}>
            {clientId === undefined && clients ? (
                <section className="flex flex-col gap-2">
                    <SectionLabel inline={inline} htmlFor={`${ids}-client`}>
                        {t("admin.alumni.logContact.clientLabel")}
                    </SectionLabel>
                    <ClientSearchField
                        id={`${ids}-client`}
                        clients={clients}
                        value={pickedClientId}
                        onValueChange={setPickedClientId}
                    />
                </section>
            ) : null}

            {askChannel ? (
                <section className="flex flex-col gap-2">
                    <SectionLabel inline={inline} id={`${ids}-channel`}>
                        {t("admin.alumni.logContact.channelLabel")}
                    </SectionLabel>
                    <ChoiceChips
                        aria-labelledby={`${ids}-channel`}
                        value={channel ?? ""}
                        onValueChange={(value) => setPickedChannel(value as ContactChannel)}
                        options={CONTACT_CHANNELS.map((value) => ({
                            value,
                            label: t(`admin.alumni.channel.${value}`),
                        }))}
                    />
                </section>
            ) : null}

            {askOutcome ? (
                <section className="flex flex-col gap-2">
                    <SectionLabel inline={inline} id={`${ids}-outcome`}>
                        {t("admin.alumni.logContact.outcomeLabel")}
                    </SectionLabel>
                    <ChoiceChips
                        aria-labelledby={`${ids}-outcome`}
                        value={outcome ?? ""}
                        onValueChange={(value) => setOutcome(value as ContactOutcome)}
                        options={CONTACT_OUTCOMES.map((value) => ({
                            value,
                            label: t(`admin.alumni.outcome.${value}`),
                        }))}
                    />
                </section>
            ) : null}

            {askRest ? (
                <>
                    <section className="flex flex-col gap-2">
                        <SectionLabel inline={inline} htmlFor={`${ids}-note`}>
                            {t("admin.alumni.logContact.noteLabel")}
                        </SectionLabel>
                        <Textarea
                            id={`${ids}-note`}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder={t("admin.alumni.logContact.notePlaceholder")}
                        />
                    </section>

                    <section className="flex flex-col gap-2">
                        <SectionLabel inline={inline} id={`${ids}-follow-up`}>
                            {t("admin.alumni.logContact.followUpLabel")}
                        </SectionLabel>
                        <FollowUpPicker
                            // The dialog's field is at its foot, with room only above; the row's is not.
                            calendarSide={inline ? "below" : "above"}
                            labelledBy={`${ids}-follow-up`}
                            choices={FOLLOW_UP_CHOICES}
                            choice={followUp}
                            onChoiceChange={setFollowUp}
                            customDate={customDate}
                            onCustomDateChange={setCustomDate}
                            today={today}
                        />
                    </section>
                </>
            ) : null}

            {inline ? (
                // Bottom right, ending on the same edge as the row's Cancel: where the eye lands after the last question.
                <div className="flex flex-wrap items-center justify-end gap-2.5">
                    {onOpenFullLog ? (
                        <Button
                            variant="ghost"
                            className={cn(
                                PILL_BUTTON,
                                "text-primary px-2 font-medium hover:bg-transparent hover:underline",
                            )}
                            rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
                            onClick={onOpenFullLog}
                        >
                            {t("admin.alumni.queue.openFullLog")}
                        </Button>
                    ) : null}
                    {askRest ? submitButton : null}
                </div>
            ) : (
                <DialogFooter>
                    <Button variant="ghost" onClick={onCancel}>
                        {t("common.actions.cancel")}
                    </Button>
                    {submitButton}
                </DialogFooter>
            )}
        </div>
    );
}

/** The dialog labels its questions in bold text, the queue's row in the mockups' mono eyebrow. */
function SectionLabel({
    inline,
    id,
    htmlFor,
    children,
}: {
    inline: boolean;
    id?: string;
    htmlFor?: string;
    children: ReactNode;
}) {
    const content = htmlFor ? <label htmlFor={htmlFor}>{children}</label> : children;
    return inline ? (
        <Eyebrow as="div" id={id}>
            {content}
        </Eyebrow>
    ) : (
        <Text as="div" id={id} size="sm" weight="semibold">
            {content}
        </Text>
    );
}
